import React, { useRef } from "react";
import TagControls from "./TagControls";
import SaveAllButton from "./SaveAllButton";
import WipeAllButton from "./WipeAllButton";
import gearIcon from "../svg/gear.svg";
import { lang } from "../lang";

function SettingsDrawer({
  settings,
  toggleSetting,
  updateSetting,
  deleteTag,
  onUpdateTasksWithDeletedTag,
  onCleanUpCompletedTasks,
  onSaveAllAndRefresh,
  hasUnsavedChanges,
  handleImportData,
  setShowModal,
  isDrawerOpen,
  setIsDrawerOpen,
  onAddSampleTasks, // NEW: Destructure the prop passed from App.jsx
}) {
  const fileInputRef = useRef(null);

  const toggleDrawer = (e) => {
    if (
      e.target.closest(".drawer-toggle") ||
      e.target.classList.contains("drawer")
    ) {
      if (
        settings.keepDrawerOpen &&
        isDrawerOpen &&
        e.target.classList.contains("drawer")
      ) {
        return;
      }
      setIsDrawerOpen(!isDrawerOpen);
    }
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const handleDefaultDurationChange = (e) => {
    e.stopPropagation();
    const duration = parseInt(e.target.value, 10);
    updateSetting("defaultTaskDuration", duration);
  };

  const handleClockTimeFormatChange = (e) => {
    e.stopPropagation();
    updateSetting("clockTimeFormat", e.target.value);
  };

  const handleClockFontThemeChange = (e) => {
    e.stopPropagation();
    updateSetting("clockFontTheme", e.target.value);
  };

  const handleCleanUpClick = (e) => {
    e.stopPropagation();
    if (
      window.confirm(
        "Are you sure you want to remove all completed tasks? This action cannot be undone." // Consider using lang key: lang.confirmations.cleanUp
      )
    ) {
      onCleanUpCompletedTasks();
    }
  };

  return (
    <div
      className={`drawer ${isDrawerOpen ? "open" : ""}`}
      onClick={toggleDrawer}
    >
      <div className="drawer-content" onClick={stopPropagation}>
        <div className="drawer-options">
          <h3 className="drawer-heading">{lang.drawer.heading}</h3>
          {isDrawerOpen && (
            <>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.showClocks}
                  onChange={() => toggleSetting("showClocks")}
                />
                {lang.drawer.showClocks}
              </label>
              {settings.showClocks && (
                <>
                  <label className="select-option">
                    {lang.drawer.clockTimeFormat}
                    <select
                      value={settings.clockTimeFormat}
                      onChange={handleClockTimeFormatChange}
                      className="settings-select"
                    >
                      <option value="12h">
                        {lang.drawer.clockTimeFormatOptions.twelveHour}
                      </option>
                      <option value="24h">
                        {lang.drawer.clockTimeFormatOptions.twentyFourHour}
                      </option>
                    </select>
                  </label>
                  <label className="select-option">
                    {lang.drawer.clockFontTheme}
                    <select
                      value={settings.clockFontTheme}
                      onChange={handleClockFontThemeChange}
                      className="settings-select"
                    >
                      <option value="default">
                        {lang.drawer.clockFontThemeOptions.default}
                      </option>
                      <option value="digital">
                        {lang.drawer.clockFontThemeOptions.digital}
                      </option>
                    </select>
                  </label>
                </>
              )}
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.useTaskPriority}
                  onChange={() => toggleSetting("useTaskPriority")}
                />
                {lang.drawer.useTaskPriority}
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.saveOnBrowserClose}
                  onChange={() => toggleSetting("saveOnBrowserClose")}
                />
                {lang.drawer.saveOnBrowserClose}
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.useTags}
                  onChange={() => toggleSetting("useTags")}
                />
                {lang.drawer.useTags}
              </label>
              {settings.useTags && (
                <TagControls
                  settings={settings}
                  updateSetting={updateSetting}
                  deleteTag={deleteTag}
                  onUpdateTasksWithDeletedTag={onUpdateTasksWithDeletedTag}
                />
              )}
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.useNotes}
                  onChange={() => toggleSetting("useNotes")}
                />
                {lang.drawer.useNotes}
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.addTaskOnEnter}
                  onChange={() => toggleSetting("addTaskOnEnter")}
                />
                {lang.drawer.addTaskOnEnter}
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.autoCompleteOnTimer}
                  onChange={() => toggleSetting("autoCompleteOnTimer")}
                />
                {lang.drawer.autoCompleteOnTimer}
              </label>
              {/* <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.showLegend}
                  onChange={() => toggleSetting("showLegend")}
                />
                {lang.drawer.showLegend}
              </label> */}
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.keepDrawerOpen}
                  onChange={() => toggleSetting("keepDrawerOpen")}
                />
                {lang.drawer.keepDrawerOpen || "Keep Drawer Open"}{" "}
                {/* Assuming lang key might be missing, providing fallback */}
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={!!settings.useDailyTasks}
                  onChange={() => toggleSetting("useDailyTasks")}
                />
                {lang.drawer.useDailyTasks || "Use Daily Tasks"}{" "}
                {/* Assuming lang key might be missing */}
              </label>
              <label className="default-task-duration">
                {lang.drawer.defaultTaskDuration}
                <select
                  value={settings.defaultTaskDuration}
                  onChange={handleDefaultDurationChange}
                  className="settings-select"
                >
                  <option value={900}>
                    {lang.drawer.defaultTaskDurationOptions.fifteenMinutes}
                  </option>
                  <option value={1200}>
                    {lang.drawer.defaultTaskDurationOptions.twentyMinutes}
                  </option>
                  <option value={1800}>
                    {lang.drawer.defaultTaskDurationOptions.thirtyMinutes}
                  </option>
                </select>
              </label>

              <div className="drawer-button-group">
                <WipeAllButton onClick={() => setShowModal(true)} />
                <SaveAllButton
                  onClick={onSaveAllAndRefresh}
                  hasUnsavedChanges={hasUnsavedChanges}
                  animationType="border-chase"
                />
              </div>

              <input
                type="file"
                accept=".json"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleImportData}
              />
              <button
                className="drawer-action-button"
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
              >
                {lang.drawer.importData}
              </button>
              <button
                className="cleanup-completed-tasks-button drawer-action-button" // Added drawer-action-button for consistency
                onClick={handleCleanUpClick}
              >
                {lang.drawer.cleanUpCompletedTasks}
              </button>

              {/* NEW BUTTON to add sample tasks */}
              <button
                className="drawer-action-button"
                onClick={onAddSampleTasks}
              >
                {lang.drawer.loadSampleTasks || "Load Sample Tasks Guide"}{" "}
                {/* Use lang key or fallback text */}
              </button>
            </>
          )}
        </div>
        <button className="drawer-toggle" onClick={toggleDrawer}>
          <img src={gearIcon} alt={lang.drawer.settings} />
          {isDrawerOpen && (
            <span className="settings-label">{lang.drawer.settings}</span>
          )}
        </button>
      </div>
    </div>
  );
}

export default SettingsDrawer;
